import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgentRun, agentRunsAPI, RunLogEntry } from '../../api/client';
import { legacyErrorText } from '../../api/errors';
import { ExpandableText } from '../ExpandableText';
import { RECONNECT, useEventStream } from '../../hooks/useEventStream';
import { useViewport } from '../../hooks/useViewport';
import { Chip, buttonStyle } from '../ui';
import type { ChipTone } from '../ui';

const TERMINAL_STATUSES = ['succeeded', 'failed', 'timed_out', 'cancelled'];

// Statuses the backend accepts on POST /agent-runs/{id}/retry (succeeded is
// deliberately excluded — retrying success invites duplicate side effects).
const RETRYABLE_STATUSES = ['failed', 'timed_out', 'cancelled'];

// A finished crew run's log can still grow: the server appends its notes (a
// hand-off refused, successors the budget did not launch) just after the
// terminal status, on the stream this panel closes at that status. So a
// terminal status catches the log up at once, and once more this long after,
// for a note the server was still writing.
const FINISHED_LOG_SETTLE_MS = 2000;

// Human-readable label + color for a run's structured failure class (issue
// #184). Retryable classes (provider_unavailable, timeout, worker_error) read
// amber; terminal-by-design ones (auth, agent_error, workspace) read red.
const ERROR_CLASS_META: Record<string, { label: string; tone: ChipTone; retryable: boolean }> = {
  provider_unavailable: { label: 'provider unavailable', tone: 'warning', retryable: true },
  timeout: { label: 'timeout', tone: 'warning', retryable: true },
  worker_error: { label: 'worker error', tone: 'warning', retryable: true },
  auth: { label: 'auth', tone: 'danger', retryable: false },
  workspace: { label: 'workspace', tone: 'danger', retryable: false },
  agent_error: { label: 'agent error', tone: 'danger', retryable: false },
};

export const errorClassMeta = (
  errorClass?: string | null
): { label: string; tone: ChipTone; retryable: boolean } | null => {
  if (!errorClass) return null;
  return ERROR_CLASS_META[errorClass] || { label: errorClass, tone: 'danger', retryable: false };
};

// ErrorClassChip renders a run's failure class as a small pill (nothing for a
// run that succeeded or was cancelled — those carry no class).
export const ErrorClassChip: React.FC<{ errorClass?: string | null; title?: string }> = ({
  errorClass,
  title,
}) => {
  const meta = errorClassMeta(errorClass);
  if (!meta) return null;
  return (
    <span style={{ marginLeft: 6 }}>
      <Chip tone={meta.tone} title={title || `failure class: ${meta.label}${meta.retryable ? ' (retryable)' : ''}`}>
        {meta.label}
      </Chip>
    </span>
  );
};

/** The chip tone of an agent run's status. */
export const runStatusTone = (status: string): ChipTone => {
  switch (status) {
    case 'queued':
    case 'claimed':
    case 'running':
      return 'info';
    case 'awaiting_approval':
      return 'warning';
    case 'succeeded':
      return 'success';
    case 'failed':
    case 'timed_out':
      return 'danger';
    default:
      return 'neutral';
  }
};

// What the run's stream reaches of the panel's log effect, all of it the
// current run's: the log it appends to, the catch-up and the polling it falls
// back on, and the finish a terminal status starts.
interface RunFeed {
  appendLogs: (entries: RunLogEntry[]) => void;
  catchUp: () => void;
  startPolling: () => void;
  finished: () => void;
}

interface RunDetailPanelProps {
  runId: string;
  onSelectRun: (runId: string) => void;
  onClose: () => void;
}

const logLine = (entry: RunLogEntry, idx: number): React.ReactNode => {
  const p = entry.payload || {};
  switch (entry.kind) {
    case 'tool_call': {
      const name = p.name || p.tool || 'tool';
      const args = p.args ?? p.input ?? {};
      let argsText = '';
      try {
        argsText = typeof args === 'string' ? args : JSON.stringify(args);
      } catch {
        argsText = String(args);
      }
      return (
        <div
          key={`${entry.seq}-${idx}`}
          style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)', margin: '3px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          → tool: {name}(<ExpandableText text={argsText} limit={300} />)
        </div>
      );
    }
    case 'tool_result': {
      let text = p.text || p.result || p.content || '';
      if (typeof text !== 'string') {
        try {
          text = JSON.stringify(text);
        } catch {
          text = String(text);
        }
      }
      return (
        <div
          key={`${entry.seq}-${idx}`}
          style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--neutral)', margin: '3px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          <ExpandableText text={text} limit={400} />
        </div>
      );
    }
    case 'error':
      return (
        <div
          key={`${entry.seq}-${idx}`}
          style={{ fontSize: 12.5, color: 'var(--danger)', margin: '3px 0', whiteSpace: 'pre-wrap' }}
        >
          {p.text || p.error || p.message || JSON.stringify(p)}
        </div>
      );
    case 'marker':
      // A notice about the run rather than its agent's output: the worker's
      // own (events it dropped) or a note the server keeps on a finished
      // crew run (a hand-off refused, successors the budget did not launch).
      // Its message says it in words.
      return (
        <div
          key={`${entry.seq}-${idx}`}
          style={{ fontSize: 12.5, color: 'var(--warning-text)', margin: '3px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {typeof p.message === 'string' && p.message ? p.message : JSON.stringify(p)}
        </div>
      );
    default:
      return (
        <div
          key={`${entry.seq}-${idx}`}
          style={{ fontSize: 13, color: 'var(--text)', margin: '4px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          <ExpandableText text={typeof p.text === 'string' ? p.text : JSON.stringify(p)} limit={1500} />
        </div>
      );
  }
};

// Cancel and Retry are the same control in two shapes: a compact chip in the
// title row on a pointer-sized screen, a full-width tappable row on a phone.
const cancelStyle = (phone: boolean): React.CSSProperties => ({
  ...buttonStyle('danger', { compact: !phone }),
  ...(phone ? { minHeight: 44, fontSize: 14 } : {}),
});

const retryStyle = (phone: boolean, retrying: boolean): React.CSSProperties => ({
  ...buttonStyle('primary', { compact: !phone, disabled: retrying }),
  ...(phone ? { minHeight: 44, fontSize: 14 } : {}),
});

export const RunDetailPanel: React.FC<RunDetailPanelProps> = ({ runId, onSelectRun, onClose }) => {
  // On a phone this panel is a full-screen sheet: Cancel and Retry move out
  // of the title row into a full-width, 44 px pair under it, so watching a
  // run and acting on it are both one-handed (REQ-108).
  const { isPhone } = useViewport();
  const [run, setRun] = useState<AgentRun | null>(null);
  const [logs, setLogs] = useState<RunLogEntry[]>([]);
  const [tree, setTree] = useState<AgentRun[]>([]);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const lastSeqRef = useRef(0);
  const statusRef = useRef('');
  const feedRef = useRef<RunFeed | null>(null);

  const loadRun = useCallback(() => {
    agentRunsAPI
      .get(runId)
      .then((res) => {
        setRun(res.data);
        statusRef.current = res.data.status;
      })
      .catch((err) => setError(legacyErrorText(err, 'Failed to load run')));
    agentRunsAPI
      .tree(runId)
      .then((res) => setTree(res.data || []))
      .catch(() => setTree([]));
  }, [runId]);

  // The run's log, its catch-up and the polling fallback, with their timers,
  // for the stream below.
  useEffect(() => {
    setLogs([]);
    lastSeqRef.current = 0;
    statusRef.current = '';
    loadRun();

    let pollTimer: number | null = null;
    let settleTimer: number | null = null;
    let closed = false;

    const appendLogs = (entries: RunLogEntry[]) => {
      if (entries.length === 0) return;
      setLogs((prev) => {
        const seen = new Set(prev.map((l) => l.seq));
        const fresh = entries.filter((l) => !seen.has(l.seq));
        if (fresh.length === 0) return prev;
        const next = [...prev, ...fresh].sort((a, b) => a.seq - b.seq);
        lastSeqRef.current = next[next.length - 1]?.seq || lastSeqRef.current;
        return next;
      });
    };

    const catchUp = () => {
      agentRunsAPI
        .logs(runId, lastSeqRef.current)
        .then((res) => appendLogs(res.data || []))
        .catch(() => undefined);
    };

    const startPolling = () => {
      if (pollTimer !== null || closed) return;
      const poll = () => {
        agentRunsAPI
          .logs(runId, lastSeqRef.current)
          .then((res) => appendLogs(res.data || []))
          .catch(() => undefined);
        agentRunsAPI
          .get(runId)
          .then((res) => {
            setRun(res.data);
            statusRef.current = res.data.status;
            if (TERMINAL_STATUSES.includes(res.data.status) && pollTimer !== null) {
              window.clearInterval(pollTimer);
              pollTimer = null;
            }
          })
          .catch(() => undefined);
      };
      poll();
      pollTimer = window.setInterval(poll, 3000);
    };

    // A terminal status: refresh once for final text / tokens, and catch the
    // log up for the server's notes (FINISHED_LOG_SETTLE_MS).
    const finished = () => {
      loadRun();
      catchUp();
      if (settleTimer === null) {
        settleTimer = window.setTimeout(() => {
          settleTimer = null;
          if (!closed) catchUp();
        }, FINISHED_LOG_SETTLE_MS);
      }
    };

    feedRef.current = { appendLogs, catchUp, startPolling, finished };

    return () => {
      closed = true;
      feedRef.current = null;
      if (settleTimer !== null) window.clearTimeout(settleTimer);
      if (pollTimer !== null) window.clearInterval(pollTimer);
    };
  }, [runId, loadRun]);

  // Log streaming with polling fallback. Each (re)connect resumes from the
  // last seq we saw, so nothing is lost across drops. After a drop the stream
  // is retried 3 times with exponential backoff (RECONNECT.backoff: 1, 2 and
  // 4 s), each after a catch-up, before the panel settles on the 3s polling
  // fallback for good; a live event restores the budget. The effect above
  // runs first, so a new run's stream opens from seq 0.
  useEventStream(
    agentRunsAPI.streamUrl(runId),
    {
      log: (evt) => {
        try {
          const entry: RunLogEntry = JSON.parse(evt.data);
          feedRef.current?.appendLogs([entry]);
        } catch {
          // ignore malformed events
        }
      },
      partial: (evt) => {
        // The answer as the agent writes it; replaced by final_text at
        // finish. Always the whole text, so a dropped frame is harmless.
        try {
          const data = JSON.parse(evt.data);
          const text = typeof data === 'string' ? data : data.text || '';
          setRun((prev) => (prev ? { ...prev, partial_text: text } : prev));
        } catch {
          // ignore malformed events
        }
      },
      status: (evt, stream) => {
        let status = '';
        try {
          const data = JSON.parse(evt.data);
          status = typeof data === 'string' ? data : data.status || '';
          setRun((prev) => (prev ? { ...prev, ...((typeof data === 'object' && data) || {}), status } : prev));
        } catch {
          status = evt.data;
          setRun((prev) => (prev ? { ...prev, status } : prev));
        }
        statusRef.current = status;
        if (TERMINAL_STATUSES.includes(status)) {
          stream.close();
          feedRef.current?.finished();
        }
      },
    },
    {
      withCredentials: true,
      reconnect: {
        ...RECONNECT.backoff,
        // Catch up on anything missed while disconnected, then retry SSE.
        onRetry: () => feedRef.current?.catchUp(),
        // Reconnect budget exhausted, or no EventSource at all: settle on polling.
        onGiveUp: () => feedRef.current?.startPolling(),
        isDone: () => TERMINAL_STATUSES.includes(statusRef.current),
      },
      resumeUrl: () => agentRunsAPI.streamUrl(runId, lastSeqRef.current),
    }
  );

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs.length]);

  const cancel = async () => {
    try {
      const res = await agentRunsAPI.cancel(runId);
      setRun(res.data);
    } catch (err) {
      setError(legacyErrorText(err, 'Failed to cancel run'));
    }
  };

  const retry = async () => {
    setRetrying(true);
    setError('');
    try {
      // The backend enqueues a NEW run (same agent/prompt/project, launched
      // by the current user); jump the panel to it.
      const res = await agentRunsAPI.retry(runId);
      onSelectRun(res.data.id);
    } catch (err) {
      setError(legacyErrorText(err, 'Failed to retry run'));
    } finally {
      setRetrying(false);
    }
  };

  const renderTree = (nodes: AgentRun[], parentId: string | null, depth: number): React.ReactNode =>
    nodes
      .filter((n) => (n.parent_run_id || null) === parentId)
      .map((n) => (
        <React.Fragment key={n.id}>
          <div
            onClick={() => onSelectRun(n.id)}
            style={{
              paddingLeft: depth * 18,
              padding: `4px 6px 4px ${6 + depth * 18}px`,
              cursor: 'pointer',
              fontSize: 13,
              background: n.id === runId ? 'var(--tint-blue)' : 'transparent',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ color: 'var(--text)' }}>{n.agent_name || n.agent_id}</span>
            <Chip tone={runStatusTone(n.status)}>{n.status}</Chip>
          </div>
          {renderTree(nodes, n.id, depth + 1)}
        </React.Fragment>
      ));

  const isLive = run && !TERMINAL_STATUSES.includes(run.status);
  const isRetryable = run && RETRYABLE_STATUSES.includes(run.status);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--neutral-soft)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <strong style={{ flex: 1, fontSize: 14, color: 'var(--text)' }}>
          {run?.agent_name || 'Run'}{' '}
          {run && (
            <span style={{ marginLeft: 6 }}>
              <Chip tone={runStatusTone(run.status)}>{run.status}</Chip>
            </span>
          )}
          {run && <ErrorClassChip errorClass={run.error_class} />}
        </strong>
        {!isPhone && isLive && (
          <button onClick={cancel} style={cancelStyle(false)}>
            Cancel
          </button>
        )}
        {!isPhone && isRetryable && (
          <button
            onClick={retry}
            disabled={retrying}
            title="Re-run this prompt as a new run with the same agent and project"
            style={retryStyle(false, retrying)}
          >
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
        <button
          onClick={onClose}
          aria-label="Close run detail"
          style={{
            background: 'none',
            border: 'none',
            fontSize: isPhone ? 24 : 18,
            lineHeight: 1,
            cursor: 'pointer',
            color: 'var(--text-muted)',
            // A finger needs 44 px; a mouse still needs more than the 11 px
            // the glyph drew on its own.
            minWidth: isPhone ? 44 : 28,
            minHeight: isPhone ? 44 : 28,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      {isPhone && (isLive || isRetryable) && (
        <div className="action-sheet" style={{ padding: '10px 12px 0' }}>
          {isLive && (
            <button onClick={cancel} style={cancelStyle(true)}>
              Cancel this run
            </button>
          )}
          {isRetryable && (
            <button
              onClick={retry}
              disabled={retrying}
              title="Re-run this prompt as a new run with the same agent and project"
              style={retryStyle(true, retrying)}
            >
              {retrying ? 'Retrying…' : 'Retry this run'}
            </button>
          )}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{error}</div>}

        {run && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.7 }}>
            <div>
              Started: {run.started_at ? new Date(run.started_at).toLocaleString() : '—'} · Tokens:{' '}
              {run.tokens_in + run.tokens_out}
              {run.cost_usd != null && ` · Cost: $${run.cost_usd.toFixed(4)}`}
            </div>
            {(run.agent_model || run.agent_effort || run.agent_content_hash) && (
              <div
                title="The agent identity this run was launched with, snapshotted at launch so it stays fixed even if the agent is edited later."
              >
                Executed with: {run.agent_model || 'default model'}
                {run.agent_effort && ` · effort ${run.agent_effort}`}
                {run.agent_content_hash && ` · agent ${run.agent_content_hash.slice(0, 12)}`}
              </div>
            )}
            {run.retried_from_run_id && (
              <div>
                Retry of{' '}
                <span
                  onClick={() => onSelectRun(run.retried_from_run_id!)}
                  style={{ color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  an earlier run
                </span>
              </div>
            )}
            {run.max_attempts != null && run.max_attempts > 1 && run.attempt_count != null && (
              <div title="Bounded auto-retry: a retryable failure re-enqueues a fresh attempt until this budget is spent.">
                Attempt {run.attempt_count} of {run.max_attempts}
              </div>
            )}
            <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-body)' }}>Prompt: {run.prompt}</div>
          </div>
        )}

        {tree.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
              Run tree
            </div>
            {renderTree(tree, null, 0)}
          </div>
        )}

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Log</div>
        <div
          style={{
            background: 'var(--surface-alt)',
            border: '1px solid var(--neutral-soft)',
            borderRadius: 4,
            padding: 10,
            minHeight: 120,
          }}
        >
          {logs.length === 0 && <div style={{ color: 'var(--neutral-mid)', fontSize: 12 }}>No log output yet…</div>}
          {logs.map((entry, idx) => logLine(entry, idx))}
          <div ref={logsEndRef} />
        </div>

        {run?.error && (
          <div
            style={{
              marginTop: 12,
              background: 'var(--tint-red)',
              border: '1px solid var(--danger)',
              color: 'var(--danger-strong)',
              borderRadius: 4,
              padding: '8px 12px',
              fontSize: 12.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {run.error}
          </div>
        )}

        {!run?.final_text && run?.partial_text && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
              Output so far
            </div>
            <div
              style={{
                background: 'var(--surface-alt)',
                border: '1px solid var(--neutral-soft)',
                borderRadius: 4,
                padding: '10px 12px',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                color: 'var(--text)',
              }}
            >
              {run.partial_text}
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: 7,
                  marginLeft: 2,
                  borderBottom: '2px solid var(--text-muted)',
                  verticalAlign: 'baseline',
                }}
              />
            </div>
          </div>
        )}

        {run?.final_text && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
              Final output
            </div>
            <div
              style={{
                background: 'var(--tint-green)',
                border: '1px solid var(--tint-green-border)',
                borderRadius: 4,
                padding: '10px 12px',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                color: 'var(--text)',
              }}
            >
              {run.final_text}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
